import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle, Send, Mic, Image, Smile,
  MoreVertical, Search, Plus, Users, ArrowLeft,
  Pin, Archive, Trash2, VolumeX, Volume2,
  Check, CheckCheck, X, Settings, LogOut, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emoji picker simple
const QUICK_EMOJIS = [
  '👍','❤️','😂','😮','😢','🔥','🎉','👏',
  '😊','😍','🤩','😎','🤔','😅','🙏','💯',
  '🍕','🍜','🥘','🍳','👨‍🍳','🌶️','🧆','🥙',
];

export default function MessagesPage() {
  useTranslation();
  const { token, user } = useAuth();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [, setShowGroupSettings] = useState(false);
  const [, setUnreadCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [typingUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const messagesContainerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const pollingRef = useRef(null);
  const convoPollingRef = useRef(null);
  const messageCacheRef = useRef({});
  const fetchAbortRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messaging/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [token]);
  
  const scrollToBottom = useCallback((behavior = 'auto') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId, { silent = false } = {}) => {
    if (!token || !conversationId) return;

    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    if (!silent) setMessagesLoading(true);

    try {
      const res = await fetch(
        `${API_URL}/api/messaging/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );

      if (res.ok) {
        const data = await res.json();
        const fetched = data.messages || [];

        messageCacheRef.current[conversationId] = fetched;
        setMessages(fetched);

        if (!silent) {
          setTimeout(() => scrollToBottom(), 50);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Failed to fetch messages:', err);
    } finally {
      if (!controller.signal.aborted) setMessagesLoading(false);
    }
  }, [token, scrollToBottom]);

  const handleSelectConversation = useCallback((conv) => {
    // Cancel any in-flight fetch for the previous conversation
    if (fetchAbortRef.current) fetchAbortRef.current.abort();

    // Clear polling for previous conversation
    if (pollingRef.current) clearInterval(pollingRef.current);

    const cached = messageCacheRef.current[conv.id];
    if (cached) {
      // Show cached messages instantly, fetch in background to refresh
      setMessages(cached);
      setMessagesLoading(false);
      setTimeout(() => scrollToBottom(), 50);
      fetchMessages(conv.id, { silent: true });
    } else {
      // No cache: clear stale messages immediately and show loader
      setMessages([]);
      fetchMessages(conv.id);
    }

    setSelectedConversation(conv);
    window.scrollTo(0, 0);
  }, [fetchMessages, scrollToBottom]);

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, [fetchConversations, fetchUnreadCount]);

  useEffect(() => {
    if (!selectedConversation) return;

    // Poll messages every 3s (silent — no loading state, no scroll jump)
    pollingRef.current = setInterval(() => {
      fetchMessages(selectedConversation.id, { silent: true });
    }, 3000);

    // Poll conversation list every 10s
    convoPollingRef.current = setInterval(() => {
      fetchConversations();
    }, 10000);

    return () => {
      clearInterval(pollingRef.current);
      clearInterval(convoPollingRef.current);
    };
  }, [selectedConversation, fetchMessages, fetchConversations]);
  
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;
    setShowEmojiPicker(false);
    setSendingMessage(true);
    
    try {
      const res = await fetch(`${API_URL}/api/messaging/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newMessage,
          message_type: 'text'
        })
      });
      
      if (res.ok) {
        setNewMessage('');
        await fetchMessages(selectedConversation.id, { silent: true });
        scrollToBottom();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSendingMessage(false);
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          
          try {
            const res = await fetch(`${API_URL}/api/messaging/conversations/${selectedConversation.id}/voice`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                audio_data: base64Audio,
                duration_seconds: recordingTime
              })
            });
            
            if (res.ok) {
              fetchMessages(selectedConversation.id, { silent: true });
            }
          } catch (err) {
            console.error('Failed to send voice message:', err);
          }
        };
        
        reader.readAsDataURL(audioBlob);
        
        // Clean up
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 120) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      audioChunksRef.current = [];
      setIsRecording(false);
      setRecordingTime(0);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  const handleReaction = async (messageId, emoji) => {
    try {
      await fetch(`${API_URL}/api/messaging/messages/${messageId}/reaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ emoji })
      });
      
      fetchMessages(selectedConversation.id, { silent: true });
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };
  
  const ConversationItem = ({ conv }) => {
    const isGroup = conv.conversation_type === 'group';
    const displayName = conv.display_name || (isGroup ? conv.group_name : conv.other_user?.display_name) || 'Unknown';
    const avatar = conv.avatar || conv.group_avatar || conv.other_user?.profile_picture;
    const isSelected = selectedConversation?.id === conv.id;
    
    return (
      <div
        onClick={() => handleSelectConversation(conv)}
        className={`flex items-center gap-3 p-3 cursor-pointer transition-all rounded-lg mx-2 ${
          isSelected 
            ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30' 
            : 'hover:bg-gray-800/50'
        }`}
        data-testid={`conversation-${conv.id}`}
      >
        <div className="relative">
          <Avatar className="h-12 w-12">
            <AvatarImage src={avatar} />
            <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
              {isGroup ? <Users className="h-5 w-5" /> : displayName?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {conv.is_pinned && (
            <Pin className="absolute -top-1 -right-1 h-4 w-4 text-orange-400" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-medium text-white truncate">{displayName}</span>
            <span className="text-xs text-gray-400">
              {conv.last_message_at && formatMessageTime(conv.last_message_at)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400 truncate">
              {conv.last_message_preview || 'Nouvelle conversation'}
            </span>
            {conv.unread_count > 0 && (
              <Badge className="bg-orange-500 text-white text-xs h-5 min-w-5 flex items-center justify-center">
                {conv.unread_count}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const MessageBubble = ({ msg, isOwn }) => {
    const [showReactions, setShowReactions] = useState(false);
    const isVoice = msg.message_type === 'audio';
    const isSystem = msg.message_type === 'system';
    
    if (isSystem) {
      return (
        <div className="flex justify-center my-2">
          <span className="text-xs text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full">
            {msg.content}
          </span>
        </div>
      );
    }
    
    return (
      <div 
        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 group`}
        data-testid={`message-${msg.id}`}
      >
        {!isOwn && (
          <Avatar className="h-8 w-8 mr-2 mt-auto">
            <AvatarImage src={msg.sender?.profile_picture} />
            <AvatarFallback className="text-xs bg-gray-700">
              {msg.sender?.display_name?.[0] || '?'}
            </AvatarFallback>
          </Avatar>
        )}
        
        <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
          {!isOwn && selectedConversation?.conversation_type === 'group' && (
            <span className="text-xs text-gray-400 ml-1 mb-1 block">
              {msg.sender?.display_name}
            </span>
          )}
          
          <div 
            className={`relative rounded-2xl px-4 py-2 ${
              isOwn 
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-br-sm' 
                : 'bg-gray-800 text-white rounded-bl-sm'
            }`}
            onDoubleClick={() => setShowReactions(true)}
          >
            {isVoice ? (
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                <div className="w-24 h-1 bg-white/30 rounded-full">
                  <div className="w-1/3 h-full bg-white rounded-full" />
                </div>
                <span className="text-xs">
                  {msg.content.match(/\d+/)?.[0] || '0'}s
                </span>
              </div>
            ) : (
              <p className="break-words">{msg.content}</p>
            )}
            
            {msg.is_pinned && (
              <Pin className="absolute -top-2 -right-2 h-4 w-4 text-yellow-400" />
            )}
          </div>
          
          {/* Reactions */}
          {Object.keys(msg.reactions || {}).length > 0 && (
            <div className={`flex gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {Object.entries(msg.reactions).map(([emoji, users]) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(msg.id, emoji)}
                  className={`text-xs px-1.5 py-0.5 rounded-full bg-gray-800 hover:bg-gray-700 border ${
                    users.includes(user?.id) ? 'border-orange-500' : 'border-gray-700'
                  }`}
                >
                  {emoji} {users.length > 1 && users.length}
                </button>
              ))}
            </div>
          )}
          
          {/* Quick reactions on hover */}
          {showReactions && (
            <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} -top-10 flex gap-1 bg-gray-900 p-2 rounded-full shadow-lg border border-gray-700`}>
              {QUICK_EMOJIS.slice(0, 6).map(emoji => (
                <button
                  key={emoji}
                  onClick={() => {
                    handleReaction(msg.id, emoji);
                    setShowReactions(false);
                  }}
                  className="hover:scale-125 transition-transform p-1"
                >
                  {emoji}
                </button>
              ))}
              <button onClick={() => setShowReactions(false)} className="text-gray-400 hover:text-white ml-1">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          <div className={`flex items-center gap-1 mt-1 text-xs text-gray-500 ${isOwn ? 'justify-end' : ''}`}>
            <span>{formatMessageTime(msg.created_at)}</span>
            {isOwn && (
              msg.is_read 
                ? <CheckCheck className="h-3 w-3 text-blue-400" />
                : <Check className="h-3 w-3" />
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const NewChatModal = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    const searchUsers = async (query) => {
      if (!query.trim()) {
        setUsers([]);
        return;
      }
      
      setLoadingUsers(true);
      try {
        const res = await fetch(`${API_URL}/api/search/users?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || []);
        }
      } catch (err) {
        console.error('Failed to search users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    const startChat = async (userId) => {
      try {
        const res = await fetch(`${API_URL}/api/messaging/conversations/direct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ user_id: userId })
        });
        
        if (res.ok) {
          const data = await res.json();
          setShowNewChatModal(false);
          fetchConversations();
          handleSelectConversation(data.conversation);
        }
      } catch (err) {
        console.error('Failed to start chat:', err);
      }
    };
    
    return (
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
            <DialogDescription className="text-gray-400">
              Recherchez un utilisateur pour démarrer une conversation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchUsers(e.target.value);
              }}
              className="bg-gray-800 border-gray-700"
            />
            
            <ScrollArea className="h-64">
              {loadingUsers ? (
                <div className="text-center py-4 text-gray-400">Recherche...</div>
              ) : users.length === 0 && searchTerm ? (
                <div className="text-center py-4 text-gray-400">Aucun utilisateur trouvé</div>
              ) : (
                <div className="space-y-2">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => startChat(u.id)}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-800 transition"
                    >
                      <Avatar>
                        <AvatarImage src={u.profile_picture} />
                        <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{u.display_name}</p>
                        <p className="text-sm text-gray-400">@{u.username}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  
  const NewGroupModal = () => {
    const [groupName, setGroupName] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [creating, setCreating] = useState(false);
    
    const searchUsers = async (query) => {
      if (!query.trim()) {
        setUsers([]);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/search/users?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users?.filter(u => u.id !== user?.id) || []);
        }
      } catch (err) {
        console.error('Failed to search users:', err);
      }
    };
    
    const createGroup = async () => {
      if (!groupName.trim() || selectedMembers.length < 1) return;
      
      setCreating(true);
      try {
        const res = await fetch(`${API_URL}/api/messaging/groups`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            name: groupName,
            member_ids: selectedMembers.map(m => m.id)
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          setShowNewGroupModal(false);
          fetchConversations();
          handleSelectConversation(data.group);
        }
      } catch (err) {
        console.error('Failed to create group:', err);
      } finally {
        setCreating(false);
      }
    };
    
    const toggleMember = (u) => {
      if (selectedMembers.find(m => m.id === u.id)) {
        setSelectedMembers(selectedMembers.filter(m => m.id !== u.id));
      } else {
        setSelectedMembers([...selectedMembers, u]);
      }
    };
    
    return (
      <Dialog open={showNewGroupModal} onOpenChange={setShowNewGroupModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Créer un groupe</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Nom du groupe"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="bg-gray-800 border-gray-700"
            />
            
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedMembers.map(m => (
                  <Badge 
                    key={m.id} 
                    className="bg-orange-500/20 text-orange-400 cursor-pointer"
                    onClick={() => toggleMember(m)}
                  >
                    {m.display_name} <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}
            
            <Input
              placeholder="Ajouter des membres..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchUsers(e.target.value);
              }}
              className="bg-gray-800 border-gray-700"
            />
            
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {users.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => toggleMember(u)}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                      selectedMembers.find(m => m.id === u.id)
                        ? 'bg-orange-500/20 border border-orange-500/30'
                        : 'hover:bg-gray-800'
                    }`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={u.profile_picture} />
                      <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span>{u.display_name}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          
          <DialogFooter>
            <Button
              onClick={createGroup}
              disabled={!groupName.trim() || selectedMembers.length < 1 || creating}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              {creating ? 'Création...' : 'Créer le groupe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };
  
  const showConversationList = !isMobile || !selectedConversation;
  const showChatView = !isMobile || selectedConversation;
  
  return (
    <div className="chat-page-height flex overflow-hidden bg-gray-950" data-testid="messages-page">
      {/* Conversation List */}
      {showConversationList && (
        <div className={`${isMobile ? 'w-full' : 'w-80 border-r border-gray-800'} flex flex-col bg-gray-900 overflow-hidden min-h-0`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold text-white">Messages</h1>
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowNewChatModal(true)}
                  className="text-gray-400 hover:text-white"
                  data-testid="new-chat-btn"
                >
                  <Plus className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowNewGroupModal(true)}
                  className="text-gray-400 hover:text-white"
                  data-testid="new-group-btn"
                >
                  <Users className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
          
          {/* Conversation list */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageCircle className="h-12 w-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Aucune conversation</p>
                <p className="text-sm text-gray-500 mt-2">Démarrez une nouvelle conversation!</p>
              </div>
            ) : (
              <div className="py-2">
                {conversations
                  .filter(c => {
                    if (!searchQuery) return true;
                    const name = c.display_name || c.group_name || c.other_user?.display_name || '';
                    return name.toLowerCase().includes(searchQuery.toLowerCase());
                  })
                  .map(conv => (
                    <ConversationItem key={conv.id} conv={conv} />
                  ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
      
      {/* Chat View */}
      {showChatView && (
        <div className="flex-1 flex flex-col bg-gray-950 overflow-hidden min-h-0">
          {selectedConversation ? (
            <>
              {/* Chat header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900">
                <div className="flex items-center gap-3">
                  {isMobile && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setSelectedConversation(null)}
                      className="text-gray-400"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                  )}
                  
                  <Avatar>
                    <AvatarImage src={selectedConversation.avatar || selectedConversation.other_user?.profile_picture} />
                    <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500">
                      {selectedConversation.conversation_type === 'group' 
                        ? <Users className="h-5 w-5" />
                        : selectedConversation.display_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div>
                    <h2 className="font-semibold text-white">
                      {selectedConversation.display_name || selectedConversation.group_name}
                    </h2>
                    {selectedConversation.conversation_type === 'group' && (
                      <p className="text-xs text-gray-400">
                        {selectedConversation.participants?.length || 0} membres
                      </p>
                    )}
                    {typingUsers.length > 0 && (
                      <p className="text-xs text-orange-400">
                        {typingUsers.length === 1 ? 'écrit...' : 'écrivent...'}
                      </p>
                    )}
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" className="text-gray-400">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-gray-900 border-gray-800 text-white">
                    {selectedConversation.is_muted ? (
                      <DropdownMenuItem className="hover:bg-gray-800 cursor-pointer">
                        <Volume2 className="h-4 w-4 mr-2" /> Activer les notifications
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem className="hover:bg-gray-800 cursor-pointer">
                        <VolumeX className="h-4 w-4 mr-2" /> Désactiver les notifications
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="hover:bg-gray-800 cursor-pointer">
                      <Archive className="h-4 w-4 mr-2" /> Archiver
                    </DropdownMenuItem>
                    {selectedConversation.conversation_type === 'group' && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-800" />
                        <DropdownMenuItem 
                          className="hover:bg-gray-800 cursor-pointer"
                          onClick={() => setShowGroupSettings(true)}
                        >
                          <Settings className="h-4 w-4 mr-2" /> Paramètres du groupe
                        </DropdownMenuItem>
                        <DropdownMenuItem className="hover:bg-gray-800 cursor-pointer text-red-400">
                          <LogOut className="h-4 w-4 mr-2" /> Quitter le groupe
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-gray-800" />
                    <DropdownMenuItem className="hover:bg-gray-800 cursor-pointer text-red-400">
                      <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 min-h-0">
                {messagesLoading && messages.length === 0 ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                        <div className={`h-10 rounded-2xl animate-pulse bg-gray-800 ${i % 2 === 0 ? 'w-2/3' : 'w-1/2'}`} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {messages.map((msg) => (
                      <MessageBubble
                        key={msg.id}
                        msg={msg}
                        isOwn={msg.sender_id === user?.id}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* Message input */}
              <div className="p-4 border-t border-gray-800 bg-gray-900">
                {isRecording ? (
                  <div className="flex items-center gap-4 bg-red-500/10 p-3 rounded-xl border border-red-500/30">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-red-400 font-medium">
                        {formatTime(recordingTime)}
                      </span>
                      <span className="text-gray-400">/2:00</span>
                    </div>
                    
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={cancelRecording}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                    
                    <Button
                      onClick={stopRecording}
                      className="bg-gradient-to-r from-orange-500 to-red-500"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-gray-400 hover:text-white shrink-0"
                    >
                      <Image className="h-5 w-5" />
                    </Button>
                    
                    <div className="flex-1 relative">
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 right-0 bg-gray-900 border border-gray-700 rounded-2xl p-2 shadow-xl z-10 w-52">
                          <div className="grid grid-cols-8 gap-1">
                            {QUICK_EMOJIS.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => {
                                  setNewMessage(prev => prev + emoji);
                                  setShowEmojiPicker(false);
                                }}
                                className="text-lg hover:scale-125 transition-transform p-0.5 rounded"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <Input
                        placeholder="Écrire un message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white pr-10"
                        data-testid="message-input"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => setShowEmojiPicker(prev => !prev)}
                        className={`absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 ${showEmojiPicker ? 'text-orange-400' : 'text-gray-400 hover:text-white'}`}
                      >
                        <Smile className="h-5 w-5" />
                      </Button>
                    </div>
                    
                    {newMessage.trim() ? (
                      <Button
                        type="submit"
                        disabled={sendingMessage}
                        className="bg-gradient-to-r from-orange-500 to-red-500 shrink-0"
                        data-testid="send-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={startRecording}
                        className="bg-gradient-to-r from-orange-500 to-red-500 shrink-0"
                        data-testid="voice-btn"
                      >
                        <Mic className="h-4 w-4" />
                      </Button>
                    )}
                  </form>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
                  <MessageCircle className="h-12 w-12 text-orange-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">Vos messages</h2>
                <p className="text-gray-400 mb-6">Sélectionnez une conversation ou démarrez-en une nouvelle</p>
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() => setShowNewChatModal(true)}
                    className="bg-gradient-to-r from-orange-500 to-red-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle conversation
                  </Button>
                  <Button
                    onClick={() => setShowNewGroupModal(true)}
                    variant="outline"
                    className="border-gray-700 text-white hover:bg-gray-800"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Créer un groupe
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Modals */}
      <NewChatModal />
      <NewGroupModal />
    </div>
  );
}
